import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { CheckoutStepComponentDef } from '@df/checkout/checkout-step.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { CheckoutService } from '@df/checkout/checkout.service';
import { App } from '@df/core/app';
import { Logger } from '@df/core/service/logger';
import { Status } from '@df/core/status';
import { IScrollToOptions } from '@df/dom/scroll/scroll-to.service';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { ScrollToErrorService } from '@df/ui/form/scroll-to-error.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { Nexus } from '@tomandco/nexus-api-types';
import find from 'lodash-es/find';
import take from 'lodash-es/take';
import { CHECKOUT_STEPS_NAMES, CheckoutFormComponent } from './checkout-form.component';

declare module '@df/checkout/checkout.interfaces' {
    namespace Checkout {
        interface IStep {
            stepGroup?: string;
        }
    }
}

@Component({
    selector: 'checkout-step',
    templateUrl: './checkout-step.html',
    standalone: true,
    imports: [CommonModule, SizeClassDirective, TranslatePipe],
    providers: [
        {
            provide: CheckoutStepComponentDef,
            useExisting: CheckoutStepComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutStepComponent extends CheckoutStepComponentDef implements OnInit, OnDestroy, Checkout.IStep {
    @Input()
    stepGroup?: string;

    @Input()
    noStatus?: boolean;

    @Input()
    statusTitle?: string;

    @Input()
    displayStatusTitle = true;

    @Input()
    onStepChange?: () => void;

    @Input()
    changeStep?: string;

    @Input()
    showLogout = false;

    @Input()
    blockStatuses?: Status[];

    @Input()
    refreshStatuses?: Status[];

    @Input()
    additionalStepStatus = false;

    logoutStatus = new Status();

    protected checkout = inject(CheckoutService);
    protected sessionStorageService = inject(SessionStorageService);
    protected requestService = inject(RequestService);

    constructor(
        checkoutFormComponent: CheckoutFormComponent,
        ngEl: ElementRef,
        app: App,
        logger: Logger,
        scrollToErrorService: ScrollToErrorService
    ) {
        super(checkoutFormComponent, ngEl, app, logger, scrollToErrorService);
    }

    override ngOnInit() {
        super.ngOnInit();

        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.StepChange).subscribe(() => {
                if (this.isCurrent && this.onStepChange) {
                    this.onStepChange();
                }

                if (this.isCurrent && this.checkoutFormComponent.currentStep?.name === Checkout.EStepName.payment) {
                    // reset express payment
                    this.sessionStorageService.remove('expressPayment');
                }

                this.detectChanges();
            })
        );

        if (this.refreshStatuses?.length) {
            this.refreshStatuses.forEach(status => {
                this.subscriptions.add(status.subscribe(() => this.onUpdate(status)));
            });
        }

        this.subscriptions.add(this.checkoutFormComponent.customer.basket.subscribe(() => this.onUpdate()));
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.subscriptions.unsubscribe();
    }

    protected onUpdate(status?: Status): void {
        if (status?.busy) {
            this.detectChanges();
        } else {
            setTimeout(() => this.detectChanges(), 100);
        }
    }

    get displayStatus(): boolean {
        const currentStepName = this.checkoutFormComponent.currentStep?.name;
        // we don't display any step status on account step
        if (currentStepName && currentStepName === CHECKOUT_STEPS_NAMES['account']) {
            return false;
        }

        return (
            (this.isValid() ||
                (this.name === CHECKOUT_STEPS_NAMES['payment'] &&
                    (this.checkoutFormComponent as CheckoutFormComponent).isPaymentStepValidForDisplay())) &&
            !this.isCurrent &&
            !this.noStatus &&
            this.arePreviousStepsValid()
        );
    }

    change(): void {
        this.goTo(this.changeStep);
    }

    override goTo(stepName?: string): void {
        let step: Checkout.IStep = this as Checkout.IStep;

        if (stepName) {
            step = this.checkoutFormComponent.steps[stepName];
        }

        this.checkoutFormComponent.goToStep(step);
    }

    logout(): Promise<Nexus.Api.Checkout.Logout.Response> {
        return this.requestService.send<Nexus.Api.Checkout.Logout.Response>(
            this.logoutStatus,
            () => this.requestService.nexus.checkout.logout({ preserve_quote: true }),
            () => {
                this.checkout.reset();
                this.checkoutFormComponent.resetData();
                this.onLogoutSuccess();
            }
        );
    }

    protected onLogoutSuccess(): void {
        this.app.events.broadcast(Checkout.EVENTS.LogoutSuccess);
    }

    get previousIsBusy(): boolean {
        if (this.blockStatuses?.length) {
            return !!find(this.blockStatuses, status => status.busy);
        }
        return false;
    }

    protected arePreviousStepsValid(): boolean {
        const steps = this.checkoutFormComponent.stepsSequence;
        const index = steps.indexOf(this);

        if (index > 0) {
            const stepsBefore = take(steps, index);
            return !find(stepsBefore, step => !step.valid);
        }

        return true;
    }

    protected override getToErrorScrollOptions(): IScrollToOptions {
        return {
            offset: Math.max(125, (this.checkoutFormComponent as CheckoutFormComponent).headerHeight + 20) // iOS hack
        };
    }
}
